import { ChangeEvent, useRef } from 'react';

import { observer } from 'mobx-react-lite';

import { Button } from '@components/button';
import { Card } from '@components/card';
import { FileBox } from '@components/file-box';
import { IconFile } from '@components/icons/file';
import { IconUpload } from '@components/icons/upload';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { abbreviateDate } from '@utils/date';

import { Box } from 'components/Box';
import { Loading } from 'components/Loading';
import { useModal } from 'features/modals';
import { useStore } from 'models/Provider';
import { IJob, IJobAttachment } from 'models/types';

interface Props {
  job: IJob;
}

export const Files = observer((props: Props) => {
  const { job } = props;
  const { lang, t } = useStore();
  const { showModal, showFullscreenModal } = useModal();
  const fileRef = useRef<HTMLInputElement>(null);

  function uploadClick() {
    fileRef !== null && fileRef.current !== null && fileRef.current.click();
  }

  function onChange(e: ChangeEvent<HTMLInputElement>) {
    const files = (e.target as HTMLInputElement).files;
    const file = files && files.length > 0 ? files[0] : null;
    const formData = new FormData();
    formData.append('attachment', file as Blob);
    job.addAttachment(formData);
  }

  function removeAttachment(attachment: IJobAttachment) {
    showModal('COMMON_REMOVE_CONFIRM', {
      _container: {
        autoWidth: false,
      },
      data: { id: attachment, confirmText: t('job.info.files.remove.confirm') },
      confirm: job.removeAttachment,
    });
  }

  return (
    <Stack space={6}>
      <Box flex h="40" alignItems="center" justifyContent="space-between">
        <Text font="bold18">{t('job.info.files.header')}</Text>
        <input style={{ display: 'none' }} type="file" ref={fileRef} onChange={onChange} />
        <Button
          variant="secondary"
          iconLeft={<IconUpload />}
          onClick={uploadClick}
          text={t('job.info.files.upload')}
        />
      </Box>
      <Separator />
      {job.uploadingAttachment && <Loading />}
      {job.attachments.length > 0 && (
        <Stack space={4}>
          {job.attachments.map((attachment: IJobAttachment) => (
            <Stack key={attachment.id} space={1}>
              <FileBox
                fileName={attachment.fileName || ''}
                onClick={() =>
                  showFullscreenModal('CUSTOM_FILE_PREVIEW', {
                    filePath: attachment.attachment,
                  })
                }
                path={attachment.attachmentOriginal}
                removeAction={(e) => removeAttachment(attachment)}
                downloadText={t('job.info.files.download')}
                removeText={t('job.info.files.remove')}
                fileObj={attachment}
              />
              {attachment.user && attachment.date && (
                <>
                  <Text font="regular14" color="purple100">
                    {t('job.info.files.addedby', attachment.user.name)}
                  </Text>
                  <Text font="regular12" color="purple60">
                    {abbreviateDate(attachment.date, lang)}
                  </Text>
                </>
              )}
            </Stack>
          ))}
        </Stack>
      )}
      {!job.attachments.length && (
        <Card spaceX={4} spaceY={4} variant="gray">
          <Stack space={2} align="center" orientation="horizontal">
            <IconFile color="purple60" />
            <Text font="semibold14" color="purple80" text={t('job.info.files.empty')} />
          </Stack>
        </Card>
      )}
      <Separator />
      <Text font="regular14" color="purple80">
        {t('job.info.files.converttext')}
      </Text>
    </Stack>
  );
});
